import React, { useCallback } from 'react'
import { intl } from 'lang/intl'
import PropTypes from 'prop-types'
import { get, isNull } from 'lodash-es'
import { FormattedMessage } from 'react-intl'
import { useCopyToClipboard } from 'react-use'

import { track } from '../../../../utils/analytics'
import {
  AjaxButtonComponent,
  AnimateComponent,
  Button as ButtonComponent,
  CenteredModalContentComponent,
} from '../../../../components'
import { CRUDContainerComponent } from '../index'
import { showNotice } from '../../../../../Layout/Notices/Notices'

import './ModeContentSwitchComponent.scss'

const getButtonLoadingProps = (text) => ({
  okText: text,
  loadingText: text,
  successText: text,
})
const getButton = (props, text) => (
  <ButtonComponent outlined {...props}>
    {text}
  </ButtonComponent>
)
const renderChancelButton = (onClick) =>
  getButton({ color: 'grey', onClick }, 'Cancel')
const ajaxButtonProps = getButtonLoadingProps('Delete Speaker')

const ModeContentSwitchComponent = ({
  crudMode,
  okButtonState,
  onSubmit,
  sizeCenteredModalIcon,
  addedSpeaker,
  openCrudModal,
  isCrudModalVisible,
  closeCrudModal,
  addCancelButton,
  isSendInviteButtonVisible,
  crudSpeakerId,
  sendInvite,
  isCrudSpeakerInviteSent,
  sendInviteSpeakerId,
}) => {
  const addedSpeakerId = get(addedSpeaker, 'live_speaker_id')
  const speakerName = get(addedSpeaker, 'name', '')
  const speakerLink = get(addedSpeaker, 'room_link', '')

  const [, copyToClipboard] = useCopyToClipboard()

  const handleCopySpeakerLink = useCallback(() => {
    if (!speakerLink) {
      return
    }

    copyToClipboard(speakerLink)
    showNotice({
      expirationMillis: 2000,
      isClosable: true,
      message: intl.formatMessage({
        defaultMessage: 'Speaker link copied!',
      }),
    })
  }, [copyToClipboard, speakerLink])

  let content

  switch (crudMode) {
    case 'edit':
    case 'new': {
      content = (
        <CRUDContainerComponent
          mode={crudMode}
          okButtonState={okButtonState}
          isVisible={isCrudModalVisible}
          onCancel={closeCrudModal}
          onSubmit={onSubmit}
          openCrudModal={openCrudModal}
          addCancelButton={addCancelButton}
          isSendInviteButtonVisible={isSendInviteButtonVisible}
          sendInvite={sendInvite}
          crudSpeakerId={crudSpeakerId}
          isCrudSpeakerInviteSent={isCrudSpeakerInviteSent}
        />
      )
      break
    }
    case 'delete': {
      content = (
        <AnimateComponent
          duration={200}
          transitionName="animation-fade"
          transitionOn={['appear']}
        >
          <div className="centered-modal-wrapper delete-speaker-content-wrapper">
            <CenteredModalContentComponent
              icon="cancel"
              size={sizeCenteredModalIcon}
            >
              <h3>
                <FormattedMessage defaultMessage="Are you sure you want to delete this speaker?" />
              </h3>
              <p>
                <FormattedMessage defaultMessage="This will remove this person from this event permanently" />
              </p>
              {renderChancelButton(closeCrudModal)}
              <AjaxButtonComponent
                color="red"
                state={okButtonState}
                {...ajaxButtonProps}
                onClick={() => onSubmit('delete', crudSpeakerId)}
              >
                <FormattedMessage defaultMessage="Delete Speaker" />
              </AjaxButtonComponent>
            </CenteredModalContentComponent>
          </div>
        </AnimateComponent>
      )
      break
    }
    case 'speaker-added': {
      content = (
        <AnimateComponent
          duration={200}
          transitionName="animation-fade"
          transitionOn={['appear']}
        >
          <div className="centered-modal-wrapper speaker-added-wrapper">
            <CenteredModalContentComponent
              icon="check"
              size={sizeCenteredModalIcon}
            >
              <h3>
                <FormattedMessage
                  defaultMessage="{speakerName} is added as a speaker"
                  values={{ speakerName }}
                />
              </h3>
              <p>
                <FormattedMessage defaultMessage="Do you want to copy the speaker link to share, or just send an invitation mail?" />
              </p>
              <div className="buttons-wrapper">
                <ButtonComponent
                  outlined
                  color="grey"
                  onClick={handleCopySpeakerLink}
                  disabled={!speakerLink}
                >
                  <FormattedMessage defaultMessage="Copy Speaker Link" />
                </ButtonComponent>
                <AjaxButtonComponent
                  state={isNull(sendInviteSpeakerId) ? 'idle' : 'loading'}
                  onClick={() => {
                    sendInvite(addedSpeakerId)
                    track('Clicked Send Invite', {
                      product: 'WebinarManagement',
                      context: 'after-create-speaker',
                    })
                  }}
                  {...getButtonLoadingProps('Send Invitation Mail')}
                >
                  <FormattedMessage defaultMessage="Send Invitation Mail" />
                </AjaxButtonComponent>
              </div>
            </CenteredModalContentComponent>
          </div>
        </AnimateComponent>
      )
      break
    }
    case 'invitation-sent': {
      content = (
        <AnimateComponent
          duration={200}
          transitionName="animation-fade"
          transitionOn={['appear']}
        >
          <div className="centered-modal-wrapper new-invite-success-wrapper">
            <CenteredModalContentComponent
              icon="check"
              size={sizeCenteredModalIcon}
            >
              <h3>
                <FormattedMessage defaultMessage="Invitation Sent Successfully" />
              </h3>
              <p>
                <FormattedMessage
                  defaultMessage="You can add additional details about your speaker <link>here</link>."
                  values={{
                    // eslint-disable-next-line react/no-unstable-nested-components
                    link: (...chunks) => (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                      <span
                        className="link"
                        onClick={() => openCrudModal('edit', addedSpeakerId)}
                      >
                        {chunks}
                      </span>
                    ),
                  }}
                />
              </p>
            </CenteredModalContentComponent>
          </div>
        </AnimateComponent>
      )
      break
    }
    default:
      content = null
  }

  return <div className="mode-content-switch-component">{content}</div>
}

ModeContentSwitchComponent.propTypes = {
  crudMode: PropTypes.oneOf([
    'new',
    'speaker-added',
    'invitation-sent',
    'edit',
    'delete',
  ]),
  onSubmit: PropTypes.func,
  openCrudModal: PropTypes.func,
  closeCrudModal: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  addedSpeaker: PropTypes.object,
  isCrudModalVisible: PropTypes.bool,
  save: PropTypes.func,
  isSendInviteButtonVisible: PropTypes.bool,
  addCancelButton: PropTypes.bool,
  sendInvite: PropTypes.func,
  sendInviteSpeakerId: PropTypes.string,
  crudSpeakerId: PropTypes.string,
  isCrudSpeakerInviteSent: PropTypes.bool,
  sizeCenteredModalIcon: PropTypes.oneOf(['small', 'normal']),
  okButtonState: PropTypes.oneOf(['idle', 'loading', 'success', 'failure']),
}

// eslint-disable-next-line import-x/no-default-export
export default ModeContentSwitchComponent
